import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import ContactContext from 'contexts/contactContext'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyInfiniteBrandLogo from 'loyalty/components/LoyaltyInfiniteBrandLogo'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import useReopenLoyaltyAccount from 'loyalty/hooks/useReopenLoyaltyAccount'
import getLoyaltyAccountClosedErrorCode from 'loyalty/lib/getLoyaltyAccountClosedErrorCode'
import getLoyaltyClosedAccountTier from 'loyalty/lib/getLoyaltyClosedAccountTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import useRebrandVersion from 'hooks/useRebrandVersion'

const HeroSection = styled.div`
  padding: ${rem(60)} 0;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

// The CTA sits below the copy, so cap the copy's width to keep the subheading to a readable line length
const HeroCopy = styled(VerticalSpacer)`
  max-width: ${rem(600)};
`

/**
 * Société landing page hero for a customer whose account is closed. Reopening keeps the account's
 * tier, so the hero wears it when the account summary tells us what it is. A customer can reopen an
 * account an admin closed themselves; when IBS can't read the membership at all, only Customer
 * Service can help, so the action calls them.
 */
function LoyaltyUserClosedAccountHero() {
  const intl = useIntl()
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const tier = useAppSelector(state => getLoyaltyClosedAccountTier(state.loyalty.account.error))
  const canReopen = useAppSelector(state => getLoyaltyAccountClosedErrorCode(state.loyalty.account.error) === 'ACCOUNT_CLOSED')
  const reopening = useAppSelector(state => !!state.loyalty.reopeningAccount)
  const contact = useContext(ContactContext)
  const isInfiniteTier = tier === 'infinite'
  const isDarkTier = tier === 'platinum' || isInfiniteTier
  const rebrandVersion = useRebrandVersion()
  const buttonVariant = isDarkTier ? (rebrandVersion === 'rebrand' ? 'ghost' : 'dark') : 'default'

  const onReopen = useReopenLoyaltyAccount()

  const societeSubheading = intl.formatMessage({
    id: '6MEv57ZFIyCmjhIOaTsr',
    defaultMessage: 'Reopen it to access your points, earn on every booking and enjoy your Société benefits again.',
    description: 'Subheading on the Société landing page hero inviting a customer with a closed account to reopen it',
  })

  const infiniteSubheading = intl.formatMessage({
    id: 'XUqb7ShNSHJqfcV0axfE',
    defaultMessage: 'Reopen it to access your points, earn on every booking and enjoy your Infinite benefits again.',
    description: 'Subheading on the Société landing page hero inviting an Infinite member with a closed Infinite account to reopen it',
  })

  const subheading = isInfiniteTier ? infiniteSubheading : societeSubheading

  const contactCustomerServiceCta = intl.formatMessage({
    id: 'UdImt9_2VWYqF3KEhPWB',
    defaultMessage: 'Contact customer service',
    description: 'Button on the Société landing page hero that phones Customer Service so a customer can reopen their closed Société account',
  })

  const reopenCta = intl.formatMessage({
    id: 'jTWsRMMSwKVs-9kt-x_A',
    defaultMessage: 'Reopen account',
    description: 'Button on the Société landing page hero that reopens a customer\'s closed Société account',
  })

  const content = <HeroSection className={tier ? `tier-${tier}` : undefined}>
    <LayoutContainer variant="medium">
      <VerticalSpacer gap={16} style={{ position: 'relative' }}>
        {isInfiniteTier && <LoyaltyInfiniteBrandLogo height={28} />}
        {!isInfiniteTier && <Group direction="horizontal" gap={12} verticalAlign="center">
          <LoyaltyProgramNameLogo width={104} />
          {tier && <LoyaltyTierLabel kind="primary" tier={tier}>
            {getLoyaltyTierDisplayName(tier)}
          </LoyaltyTierLabel>}
        </Group>}
        <Group direction="vertical" gap={24}>
          <HeroCopy gap={16}>
            <Heading variant="heading1" format="propercase">
              {isInfiniteTier && firstName && <FormattedMessage
                id="Yq408x8bppCH_UKYH0FM"
                defaultMessage="<italic>{firstName}</italic>, your Infinite{br}account is closed"
                description="Société landing page hero heading, with the customer's first name, telling an Infinite member their Infinite account is closed"
                values={{
                  firstName,
                  italic: (chunks) => <i>{chunks}</i>,
                  br: <br />,
                }}
              />}
              {isInfiniteTier && !firstName && <FormattedMessage
                id="x43hTAKJOOfHxSHe_uL4"
                defaultMessage="Your Infinite{br}account is closed"
                description="Société landing page hero heading, without a first name, telling an Infinite member their Infinite account is closed"
                values={{
                  br: <br />,
                }}
              />}
              {!isInfiniteTier && firstName && <FormattedMessage
                id="1zdUy4elzY4X3db1pHYP"
                defaultMessage="<italic>{firstName}</italic>, your Société{br}account is closed"
                description="Société landing page hero heading, with the customer's first name, telling them their Société account is closed"
                values={{
                  firstName,
                  italic: (chunks) => <i>{chunks}</i>,
                  br: <br />,
                }}
              />}
              {!isInfiniteTier && !firstName && <FormattedMessage
                id="YcJMnaksLdFiZgll7JPQ"
                defaultMessage="Your Société{br}account is closed"
                description="Société landing page hero heading, without a first name, telling the customer their Société account is closed"
                values={{
                  br: <br />,
                }}
              />}
            </Heading>
            <Heading variant="heading4">
              {subheading}
            </Heading>
          </HeroCopy>
          <Group direction="vertical" horizontalAlign="start">
            {canReopen && <TextButton kind="primary" palette="default" variant={buttonVariant} onClick={onReopen} disabled={reopening}>
              {reopenCta}
            </TextButton>}
            {!canReopen && <TextButton kind="primary" palette="default" variant={buttonVariant} href={`tel:${contact.defaultContact.number}`}>
              {contactCustomerServiceCta}
            </TextButton>}
          </Group>
        </Group>
      </VerticalSpacer>
    </LayoutContainer>
  </HeroSection>

  return tier ? <ProductPaletteProvider palette={`tier-${tier}`}>{content}</ProductPaletteProvider> : content
}

export default LoyaltyUserClosedAccountHero
